import React from 'react';
import { Box, Chip, Link, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

const relationshipColor = (relationship) => relationship?.startsWith('Exact borrower')
  ? { bgcolor: '#EAF7EF', color: '#167244', border: '#BDE7CD' }
  : { bgcolor: '#F3F6FB', color: '#50627F', border: '#DCE5F2' };

function NewsItem({ item, stress }) {
  const relationColor = relationshipColor(item.entityRelationship);
  return (
    <Box sx={{ p: 1.75, border: '1px solid', borderColor: stress ? '#F4D8C8' : '#E3EBF7', borderRadius: 2, bgcolor: stress ? '#FFFDFC' : '#FBFDFF' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.65 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: '#22395F', lineHeight: 1.3 }}>{item.headline}</Typography>
        {item.date && <Typography sx={{ whiteSpace: 'nowrap', fontSize: 12, color: '#71819A' }}>{item.date}</Typography>}
      </Box>
      <Typography sx={{ fontSize: 13.5, color: '#4D5D76', lineHeight: 1.55 }}>{item.summary}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 1.25 }}>
        <Chip size="small" label={item.entityRelationship || 'Unconfirmed relationship'} sx={{ height: 23, fontSize: 11, fontWeight: 700, bgcolor: relationColor.bgcolor, color: relationColor.color, border: `1px solid ${relationColor.border}` }} />
        <Chip size="small" label={`${item.confidence || 'medium'} confidence`} sx={{ height: 23, fontSize: 11, fontWeight: 700, bgcolor: '#fff', color: '#71819A', border: '1px solid #DCE5F2' }} />
        {item.sourceUrl ? (
          <Link href={item.sourceUrl} target="_blank" rel="noreferrer" sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: 12.5, fontWeight: 700, color: '#246BCE', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            {item.sourceName || 'Open source'} <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        ) : null}
      </Box>
    </Box>
  );
}

function NewsSection({ title, subtitle, items, stress }) {
  const Icon = stress ? WarningAmberOutlinedIcon : ArticleOutlinedIcon;
  return (
    <Box sx={{ flex: 1, minWidth: 0, p: 2.25, border: '1px solid', borderColor: stress ? '#F0D7C9' : '#D9E5F7', borderRadius: 2.5, bgcolor: '#fff' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box sx={{ display: 'grid', placeItems: 'center', width: 30, height: 30, borderRadius: 1.5, bgcolor: stress ? '#FFF3EC' : '#EEF6FF', color: stress ? '#C65C25' : '#246BCE' }}><Icon sx={{ fontSize: 18 }} /></Box>
        <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#1A2A56' }}>{title}</Typography>
      </Box>
      <Typography sx={{ minHeight: 38, mb: 1.75, fontSize: 12.75, color: '#71819A', lineHeight: 1.45 }}>{subtitle}</Typography>
      {items.length ? <Box sx={{ display: 'grid', gap: 1.1 }}>{items.map((item, index) => <NewsItem key={`${item.sourceUrl || item.headline}-${index}`} item={item} stress={stress} />)}</Box> : <Typography sx={{ py: 2, fontSize: 13.5, color: '#71819A' }}>No sourced items were found in this section for the current research window.</Typography>}
    </Box>
  );
}

export default function CompanyNewsResearch({ research }) {
  if (!research) return null;
  const latest = Array.isArray(research.latestCompanyNews) ? research.latestCompanyNews : [];
  const stress = Array.isArray(research.financialStressNews) ? research.financialStressNews : [];
  return (
    <Box sx={{ mx: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.3 }}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#1A2A56' }}>Company News Research</Typography>
          <Typography sx={{ fontSize: 13, color: '#71819A' }}>Grounded Gemini research. Related-entity items are labelled and do not affect the borrower’s score.</Typography>
        </Box>
        {research.generatedAt && <Typography sx={{ fontSize: 12, color: '#71819A' }}>Updated {new Date(research.generatedAt).toLocaleString()}</Typography>}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
        <NewsSection title="Latest company news" subtitle="Material business, operational, workforce, legal, and corporate developments." items={latest} />
        <NewsSection title="Financial stress & credit signals" subtitle="Potential liquidity, debt, ratings, restructuring, closure, and payment-risk indicators." items={stress} stress />
      </Box>
    </Box>
  );
}
